import React, { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Tooltip,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { exportMuestrasToPdf } from "./muestrasPdfExport";
import { useTranslation } from "react-i18next";

const MuestrasExportPdfDialog = ({
  muestras,
  selectedIds = [],
  buttonProps,
}) => {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleExport = async () => {
    if (!selectedIds.length || exporting) return;

    setConfirmOpen(false);
    setExporting(true);
    setExportError("");
    setProgress({ current: 0, total: 0 });
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await exportMuestrasToPdf({
        muestras: muestras || [],
        selectedIds,
        filename: `Muestras_SDS_${stamp}.pdf`,
        onProgress: setProgress,
      });
    } catch (err) {
      console.error("Error exportando PDF muestras:", err);
      setExportError(err?.message || t("Samples.pdfExportError"));
    } finally {
      setExporting(false);
    }
  };

  const progressLabel =
    progress.total > 0
      ? t("Samples.pdfGeneratingProgress", {
          current: progress.current,
          total: progress.total,
        })
      : t("Samples.pdfGenerating");

  return (
    <>
      <Tooltip
        title={
          selectedIds.length
            ? t("Samples.pdfSelectedTooltip")
            : t("Samples.pdfSelectFirstTooltip")
        }
      >
        <span>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setConfirmOpen(true)}
            disabled={exporting || selectedIds.length === 0}
            startIcon={
              exporting ? (
                <CircularProgress size={17} color="inherit" />
              ) : (
                <PictureAsPdfOutlinedIcon />
              )
            }
            {...buttonProps}
          >
            {exporting ? progressLabel : `PDF (${selectedIds.length})`}
          </Button>
        </span>
      </Tooltip>

      <Dialog
        open={confirmOpen}
        onClose={() => !exporting && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("Samples.pdfConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Samples.pdfConfirmBody", { count: selectedIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={exporting}>
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={exporting || selectedIds.length === 0}
            startIcon={<PictureAsPdfOutlinedIcon />}
          >
            {t("Samples.pdfConfirmButton")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(exportError)}
        autoHideDuration={6000}
        onClose={() => setExportError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setExportError("")}
        >
          {exportError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MuestrasExportPdfDialog;
